import { Column } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import { getAllWarehouses } from '../../../services/apis';
import useFormatMessage from '../../../utils/useFormatMessage';

const determineExitOrderType = (data, exitRecord) => {
    // Kapı çıkışından önceki son transfer emrini bul
    const lastTransferBeforeExit = [...data]
        .filter(item =>
            item.operation === "DEPO TRANSFER EMRİ" &&
            new Date(item.process_date) < new Date(exitRecord.process_date)
        )
        .sort((a, b) => new Date(b.process_date) - new Date(a.process_date))[0];

    // Kapı çıkışından önceki son geçerli faturayı bul
    const validInvoiceBeforeExit = [...data]
        .filter(item =>
            item.operation === "FATURA BİLGİSİ" &&
            item.action === "created" &&
            new Date(item.process_date) < new Date(exitRecord.process_date)
        )
        .sort((a, b) => new Date(b.process_date) - new Date(a.process_date))[0];

    if (lastTransferBeforeExit) {
        const transferDate = new Date(lastTransferBeforeExit.process_date);
        const exitDate = new Date(exitRecord.process_date);

        const hasNewInvoiceAfterTransfer = data.some(item =>
            item.operation === "FATURA BİLGİSİ" &&
            item.action === "created" &&
            new Date(item.process_date) > transferDate &&
            new Date(item.process_date) < exitDate
        );

        if (!hasNewInvoiceAfterTransfer) {
            return "TRANSFER";
        }
    }

    if (validInvoiceBeforeExit) {
        return "FATURA";
    }

    return null;
};

const Emirler = ({ data }) => {
    const { t } = useFormatMessage();
    const [dataSource, setDataSource] = useState([]);
    const [warehouses, setWarehouses] = useState([]);

    useEffect(() => {
        if (data) {
            getAllWarehouses().then(setWarehouses).catch(console.error);
        }
    }, [data]);

    useEffect(() => {
        if (!data || !Array.isArray(data)) return;

        if (warehouses.length === 0) {
            return;
        }

        const processedData = [];

        // 1. PDI - PDI Emirleri
        data.filter(item => item.operation === "PDI EMRİ")
            .forEach(pdiOrder => {
                const carModel = pdiOrder.data["car line"] || '';
                const carColor = pdiOrder.data["colour name"] || '';
                const planDate = pdiOrder.data["planlama tarihi"] || '';
                const denominationName = pdiOrder.data["commercial denomination name"] || '';

                // PDI çıkış kaydını bul
                const pdiExit = data.find(item =>
                    item.operation === "PDI ÇIKIŞ" &&
                    item.data.commercial_denomination_code === pdiOrder.data.commercial_denomination_code &&
                    new Date(item.process_date) > new Date(pdiOrder.process_date)
                );

                processedData.push({
                    GirisTarihi: moment(pdiOrder.process_date).format('DD/MM/YYYY HH:mm'),
                    GerceklesmeTarih: pdiExit ? moment(pdiExit.process_date).format('DD/MM/YYYY HH:mm') : '',
                    EmirKodu: 'PDI',
                    EmirAdi: `PDI Emri`,
                    EmirDurumu: pdiOrder.data["yeni/iptal"] === "İPTAL" ? 'İptal' :
                        pdiExit ? 'Emir Gerçekleştirildi' : 'Aktif',
                    EmirOlusturan: pdiOrder.username,
                    PlanlamaTarihi: planDate
                });
            });

        // 2. FKCK - Gümrükten Çıkış Emirleri
        data.filter(item => item.operation === "GÜMRÜK İŞLEMİ")
            .forEach(item => {
                processedData.push({
                    GirisTarihi: moment(item.process_date).format('DD/MM/YYYY HH:mm'),
                    GerceklesmeTarih: moment(item.process_date).format('DD/MM/YYYY HH:mm'),
                    EmirKodu: 'FKCK',
                    EmirAdi: 'Gümrükten Çıkış Emri',
                    EmirDurumu: 'Emir Gerçekleştirildi',
                    EmirOlusturan: item.username,
                    PlanlamaTarihi: ''
                });
            });

        // 3. YSEV - Yurt İçi Sevkiyat Emirleri
        const faturaOrders = data
            .filter(item => item.operation === "FATURA BİLGİSİ" && item.action === "created")
            .sort((a, b) => new Date(a.process_date) - new Date(b.process_date));

        // Son fatura dışındaki tüm faturaları geçersiz işaretle
        for (let i = 0; i < faturaOrders.length - 1; i++) {
            const fatura = faturaOrders[i];
            processedData.push({
                GirisTarihi: moment(fatura.process_date).format('DD/MM/YYYY HH:mm'),
                GerceklesmeTarih: '',
                EmirKodu: 'YSEV',
                EmirAdi: `Yurt İçi Sevkiyat (${fatura.data["client name"]})`,
                EmirDurumu: 'Geçersiz',
                EmirOlusturan: fatura.username,
                PlanlamaTarihi: ''
            });
        }

        // Son faturayı işle
        if (faturaOrders.length > 0) {
            const lastFatura = faturaOrders[faturaOrders.length - 1];
            const relatedExit = data.find(item =>
                item.operation === "KAPI ÇIKIŞ" &&
                new Date(item.process_date) > new Date(lastFatura.process_date) &&
                determineExitOrderType(data, item) === "FATURA"
            );

            processedData.push({
                GirisTarihi: moment(lastFatura.process_date).format('DD/MM/YYYY HH:mm'),
                GerceklesmeTarih: relatedExit ? moment(relatedExit.process_date).format('DD/MM/YYYY HH:mm') : '',
                EmirKodu: 'YSEV',
                EmirAdi: `Yurt İçi Sevkiyat (${lastFatura.data["client name"]})`,
                EmirDurumu: relatedExit ? 'Emir Gerçekleştirildi' : 'Beklemede',
                EmirOlusturan: lastFatura.username,
                PlanlamaTarihi: ''
            });
        }

        // 4. DPTR - Depo Transfer Emirleri
        const depoTransferOrders = data
            .filter(item => item.operation === "DEPO TRANSFER EMRİ")
            .sort((a, b) => new Date(a.process_date) - new Date(b.process_date));

        depoTransferOrders.forEach(depoOrder => {
            // Check if there's a cancellation record after this transfer order
            const hasTransferCancellation = data.some(item =>
                item.operation === "TRANSFER EMRİ İPTAL" &&
                new Date(item.process_date) > new Date(depoOrder.process_date)
            );

            const relatedExit = data.find(item =>
                item.operation === "KAPI ÇIKIŞ" &&
                new Date(item.process_date) > new Date(depoOrder.process_date) &&
                determineExitOrderType(data, item) === "TRANSFER"
            );

            const relatedEntry = relatedExit ? data.find(item =>
                item.operation === "DEPO GİRİŞ" &&
                new Date(item.process_date) > new Date(relatedExit.process_date)
            ) : null;

            const fromWarehouseName = warehouses && warehouses.find(w => w.id === depoOrder.data.from_warehouse_id)?.warehouse_name
                || `Depo ${depoOrder.data.from_warehouse_id}`;
            const toWarehouseName = warehouses && warehouses.find(w => w.id === depoOrder.data.to_warehouse_id)?.warehouse_name
                || `Depo ${depoOrder.data.to_warehouse_id}`;

            processedData.push({
                GirisTarihi: moment(depoOrder.process_date).format('DD/MM/YYYY HH:mm'),
                GerceklesmeTarih: relatedEntry ? moment(relatedEntry.process_date).format('DD/MM/YYYY HH:mm') : '',
                EmirKodu: 'DPTR',
                EmirAdi: `Depo Transfer (${fromWarehouseName} -> ${toWarehouseName})`,
                EmirDurumu: relatedEntry ? 'Emir Gerçekleştirildi' :
                    relatedExit ? 'Transfer Devam Ediyor' :
                        hasTransferCancellation ? 'İptal Edildi' : 'Beklemede',
                EmirOlusturan: depoOrder.username,
                PlanlamaTarihi: ''
            });
        });

        // Tüm emirleri oluşturulma tarihine göre sırala
        const sortedData = processedData.sort((a, b) =>
            new Date(a.GirisTarihi) - new Date(b.GirisTarihi)
        );

        setDataSource(sortedData);
    }, [data, warehouses]);

    return (
        <div className="w-full">
            <DatagridCustom dataSource={dataSource}>
                <Column
                    caption={t("Emir Tarihi")}
                    dataField="GirisTarihi"
                    dataType="string"
                />
                <Column
                    caption={t("Gerçekleşme")}
                    dataField="GerceklesmeTarih"
                    dataType="string"
                />
                <Column
                    caption={t("Emir Kodu")}
                    dataField="EmirKodu"
                />
                <Column
                    caption={t("Emir Adı")}
                    dataField="EmirAdi"
                />
                <Column
                    caption={t("Emir Durumu")}
                    dataField="EmirDurumu"
                />
                <Column
                    caption={t("Emir Oluşturan")}
                    dataField="EmirOlusturan"
                />
                <Column
                    caption={t("Planlama Tarihi")}
                    dataField="PlanlamaTarihi"
                    visible={true}
                />
            </DatagridCustom>
        </div>
    );
};

export default Emirler;